import { format } from 'date-fns';
import { formatCompactDuration } from './compactDuration.ts';

export const formatCreatedDate = (createdAt?: string): string => {
    if (!createdAt) return '';
    try {
        return format(new Date(createdAt), 'MMM d, yyyy');
    } catch {
        return '';
    }
};

// US numeric date ("10/01/2026"), as written into doctor-facing notes.
export const formatNumericDate = (date: Date): string => format(date, 'MM/dd/yyyy');

// Date + time, for real timestamps where the time-of-day matters (e.g. an
// OAuth token's expiry) - formatCreatedDate/formatShortDate deliberately
// drop the time.
export const formatDateTime = (value?: string | null): string => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return format(date, 'MMM d, yyyy h:mm a');
    } catch {
        return '';
    }
};

// Time of day alone ("2:05 PM"), for layouts that print the date on its own line.
export const formatTimeOfDay = (value?: string | null): string => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : format(date, 'h:mm a');
};

// Formats a DATE-ONLY value (e.g. a date of birth "2000-09-28") in LOCAL time.
// `new Date("2000-09-28")` parses as UTC midnight, which renders as the previous
// day in negative-offset timezones (the 09/28 vs 09/27 bug). We extract the
// leading Y-M-D and build a local date instead. Use this for DOB / Monday date
// columns; use formatCreatedDate for real timestamps.
export const formatDateOnly = (value?: string | null): string => {
    if (!value) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!m) return '';
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(date.getTime())) return '';
    return format(date, 'MMM d, yyyy');
};

// Compact list-view date ("Jul 20"); the year is only added when it isn't
// the current year ("Jul 20, 2024") - used for at-a-glance recency columns
// like Practices' "Last call", where the exact year rarely matters.
export const formatShortDate = (value?: string | null, now: number = Date.now()): string => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        const isCurrentYear = date.getFullYear() === new Date(now).getFullYear();
        return format(date, isCurrentYear ? 'MMM d' : 'MMM d, yyyy');
    } catch {
        return '';
    }
};

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const RECENT_DAY_CUTOFF = 30;

// Very compact "activity" recency ("2h ago", "10m ago", "3d ago"), used by
// the Users table's Last activity column. Falls back to formatShortDate past
// 30 days, since "47d ago" stops being a useful at-a-glance signal. Returns
// null (not '') for a missing value so callers can render a distinct "Never
// signed in" state instead of an empty cell.
export const formatRelativeActivity = (value?: string | null): string | null => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    if (diffMs < MINUTE_MS) return 'just now';
    if (diffMs < HOUR_MS) return `${Math.floor(diffMs / MINUTE_MS)}m ago`;
    if (diffMs < DAY_MS) return `${Math.floor(diffMs / HOUR_MS)}h ago`;
    const days = Math.floor(diffMs / DAY_MS);
    if (days < RECENT_DAY_CUTOFF) return `${days}d ago`;
    return formatShortDate(value);
};

const COMPACT_RELATIVE_DAYS = 7;

// "now", "5m", "2h", "4d" for the last week, then formatShortDate ("Oct 3", "Oct 3, 2025").
export const formatCompactRecency = (value: string | null, now: number): string => {
    const time = value ? new Date(value).getTime() : NaN;
    if (Number.isNaN(time)) return '-';
    if (now - time < COMPACT_RELATIVE_DAYS * DAY_MS) return formatCompactDuration(value, now);
    return formatShortDate(value, now);
};
